import type { Task } from "@app/core";
import { cn, Flag, Moon, Repeat } from "@app/ui";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, springs, useHoverIntent, useReducedMotion } from "../../lib/motion/index.js";
import { useMobileSheetDraftStore, useNestHoverStore, useResizePreviewStore, useSettingsStore, useTouchMoveStore } from "../../stores/index.js";
import { priorityToneClass, PRIORITY_FLAG_HEX } from "../task-colors.js";
import { useMatrixPriorityColor } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { washStyle } from "../block-wash.js";
import { useI18n } from "../../lib/useI18n.js";
import { TaskBlockContent } from "./TaskBlockContent.js";
import { fitTitle } from "./block-content-fit.js";
import type { CoverRect } from "./cover-shape.js";
import { TaskContextMenu } from "./TaskContextMenu.js";
import { InsertButton } from "./InsertButton.js";
import { beginBlockDrag, endBlockDrag, isTaskDragging, setDragTaskId, setTaskDragging, type BlockDragVisual } from "./drag.js";
import { FaintOverlay } from "./FaintOverlay.js";
import { useFlashHighlight } from "./useFlashHighlight.js";
import { useNestDrop } from "./useNestDrop.js";
import { useTaskResize } from "./useTaskResize.js";
import { useCalendarInheritedTask, useCalendarRenderIndex } from "./CalendarRenderIndex.js";
import { usePastEventFlag } from "./usePastEventFlag.js";
import { isMobileUx } from "../../lib/useMobileUx.js";
import { suppressMobileContextMenu } from "../../lib/mobile-context-menu.js";
import { mobileBlockStyle } from "../mobile/mobile-block-style.js";

interface Props {
  task: Task;
  hourHeight: number;
  /** Effective top (minutes from midnight) from nest-layout. */
  topMin: number;
  /** Effective height in minutes from nest-layout. */
  heightMin: number;
  /** True occurrence start minute (resize anchors here, not the nudged top). */
  naturalStartMin: number;
  /** Horizontal nesting inset, px. */
  leftPx: number;
  /** Column width, px (used to leave a ~10% gap on the right). */
  colWidthPx: number;
  /** Stacking depth (0 = backmost). */
  depth: number;
  /** Front block(s) covering this one's right side (content flows around them). */
  cover?: { leftPx: number; topPx: number; bottomPx: number; rects: CoverRect[] } | undefined;
  /** This block is the hover-expanded one in its column. */
  expanded: boolean;
  onExpandChange: (active: boolean) => void;
  onClick: (t: Task) => void;
  onResize: (t: Task, newDurationMinutes: number) => void;
  onResizeTop: (t: Task, newStartIso: string, newDurationMinutes: number) => void;
  /** Maps a cursor point to the grid instant under it — lets the bottom edge be
   *  dragged across days (the task grows to span them). Supplied by the grid. */
  pointToInstant?: ((clientX: number, clientY: number) => Date | null) | undefined;
  isContinuation?: boolean;
}

const POINT_HEIGHT_PX = 20;

/**
 * Exit fade, as a DYNAMIC variant so it can read `AnimatePresence`'s `custom`.
 * `bulk` is `DayColumn`'s "this commit removed a lot of blocks" verdict; see the
 * `exit` prop below for why the duration depends on it and why `custom` is the
 * only way to deliver it.
 */
const EXIT_VARIANTS = {
  none: { opacity: 1 },
  fade: (bulk: boolean) => ({
    opacity: 0,
    transition: { duration: bulk ? 0.15 : 0.4, ease: "easeOut" as const },
  }),
};

/**
 * Re-render a block only when its own DATA changes. Mounting a block is the
 * calendar's dominant per-entry cost (six store subscriptions and six hooks
 * apiece), and a dense column holds ~85 of them — so an unmemoized block turned
 * every column re-render (a hover line, a width tick, a zoom step, an arming
 * step) into a full column's worth of block renders. Function props are
 * intentionally ignored, exactly as in {@link DayColumn}: `onExpandChange` only
 * ever closes over its own stable key and `setExpandedId`, and the grid's
 * `onClick`/`onResize`/`onResizeTop`/`pointToInstant` read their task and rule
 * data LIVE (`useTasksStore.getState()`, live column rects) rather than from a
 * captured snapshot. Never reintroduce a closed-over `tasks`/`rules` into a
 * handler reached from here.
 *
 * The geometry props must therefore be reference-stable: `DayColumn` memoizes
 * `computeNestLayout` so an unchanged layout hands back the same `NestEntry`
 * objects (`cover` included).
 */
export const TaskBlock = memo(TaskBlockImpl, (a, b) =>
  a.task === b.task &&
  a.hourHeight === b.hourHeight &&
  a.topMin === b.topMin &&
  a.heightMin === b.heightMin &&
  a.naturalStartMin === b.naturalStartMin &&
  a.leftPx === b.leftPx &&
  a.colWidthPx === b.colWidthPx &&
  a.depth === b.depth &&
  a.cover === b.cover &&
  a.expanded === b.expanded &&
  a.isContinuation === b.isContinuation,
);

function TaskBlockImpl({
  task, hourHeight, topMin, heightMin, naturalStartMin, leftPx, colWidthPx, depth, cover,
  expanded, onExpandChange, onClick, onResize, onResizeTop, pointToInstant, isContinuation,
}: Props) {
  const index = useCalendarRenderIndex();
  const { t } = useI18n();
  const inheritedTask = useCalendarInheritedTask(task);
  const done = task.status === "done";
  const snoozed = task.status === "snoozed";
  const pastEvent = usePastEventFlag(task);
  const nestDrop = useNestDrop();
  const armed = useNestHoverStore((s) => s.armedId === task.id);
  // While this block is the one being long-press-moved on touch, dim it so the
  // live ghost (the thing tracking the finger) isn't doubled by the original.
  const touchMoving = useTouchMoveStore((s) => s.movingId === task.id);
  // While the mobile sheet edits this block, its schedule is represented by the
  // on-grid draft ghost — dim the original so the two aren't doubled.
  const sheetGhostSource = useMobileSheetDraftStore((s) => s.active && s.sourceId === task.id);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  // This block is the SOURCE of an in-flight drag — rendered transparent so
  // it's clear which block is being moved (the live ghost tracks the cursor).
  const [dragSource, setDragSource] = useState(false);
  // Whether expanding would actually reveal more (set by the content from its
  // collapsed metrics) — we only animate when there's hidden info to show.
  const [expandable, setExpandable] = useState(false);
  const reduce = useReducedMotion();
  // Critically-damped (no overshoot) so the block NEVER momentarily renders
  // smaller than its resting size during the collapse animation.
  const { active, reset: resetHover, handlers } = useHoverIntent(1000);
  const { ref: flashRef, flash } = useFlashHighlight(task);
  // True for every segment of the task being cross-day-resized (source + its
  // continuations): they dim while the grid paints the spanning ghost. The
  // selector returns a stable boolean, so a block re-renders only when its own
  // match flips at gesture start/end — not on every drag tick.
  const resizingThis = useResizePreviewStore((s) => s.preview?.taskId === task.id);
  const textScale = useSettingsStore((s) => s.textScale);

  // Block wash, computed up here so the cross-day resize ghost can mirror the
  // block exactly. A detached subtask with no colour of its own inherits a
  // brighter shade of its ancestor's. Continuation segments keep the priority
  // colour/border so a multi-day timed task carries the same visual priority on
  // every rendered day; only repeated adornments (flag/resize-top) are hidden.
  const contextColor = index.contextColorOf(task);
  const { tint, gradient } = index.blockStyle;
  // In matrix scope, a PLACED block's priority color is the continuous diagonal
  // hue at the task's effective (deadline-derived) position — so two positions
  // in the same discrete band still render as distinct colors.
  const priorityColor = useMatrixPriorityColor(inheritedTask);
  const { vars: customStyle, washed: hasCustomStyle } = washStyle(
    inheritedTask,
    contextColor, tint, gradient, priorityColor,
  );
  const blockVisual: BlockDragVisual = {
    task, vars: customStyle, washed: hasCustomStyle, toneClass: priorityToneClass(inheritedTask.priority, "block"),
  };

  const { previewMinutes, previewStartMin, active: resizing, resizingRef, startResize, startResizeTop } = useTaskResize({
    task, hourHeight, startMinutes: naturalStartMin, segmentMinutes: heightMin,
    pointToInstant, visual: blockVisual, onResize, onResizeTop,
  });

  // Only expand when it would reveal more, and never while dragging or resizing.
  useEffect(() => {
    onExpandChange(active && expandable && !resizing && !isTaskDragging());
  }, [active, expandable, resizing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hover-expand animates a MEASURED `height` (framer `animate`, not `layout`):
  // toggling the `layout` prop on the same render as the size change never
  // snapshotted the old bounds reliably (the expand/collapse just popped), and
  // a transform-based layout animation squished the text anyway. Animating the
  // real height reflows content natively. The target is the colored block's
  // scrollHeight, measured AFTER the expanded content has rendered.
  const [expandH, setExpandH] = useState<number | null>(null);
  // Updated in an effect so the flip render still sees the OLD value — that's
  // what lets the collapse render keep a springy transition (see `animPhase`).
  const prevExpanded = useRef(expanded);
  useEffect(() => { prevExpanded.current = expanded; }, [expanded]);

  // Measure the expanded target once the full content is in the DOM (the box is
  // still at its collapsed height then). Measure the CONTENT root's
  // scrollHeight ([data-block-content]) — the colored wrapper itself never
  // overflows (the content root clips internally), so its own scrollHeight just
  // echoes the collapsed height. +6 = the colored block's vertical padding
  // (py-0.5 → 4px) + borders (2px). Re-measures on data changes while
  // expanded; cleared on collapse.
  useLayoutEffect(() => {
    if (!expanded) { setExpandH(null); return; }
    const content = flashRef.current?.querySelector("[data-block-content]");
    if (content) setExpandH(content.scrollHeight + 6);
  }, [expanded, task, heightMin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calendar blocks always have a due_at (DayColumn filters), but guard AFTER the
  // hooks so a hypothetical dateless task can't change the hook count mid-render
  // (Rules of Hooks).
  if (!task.due_at) return null;

  const renderedDur = previewMinutes ?? heightMin;
  const renderedStartMin = previewStartMin ?? topMin;
  const top = (renderedStartMin / 60) * hourHeight;
  const renderedHeight = renderedDur > 0 ? (renderedDur / 60) * hourHeight : POINT_HEIGHT_PX;

  // Very short block: collapse the wrapper's `py-0.5` toward 0 so the title
  // gets the space first; TaskBlockContent shrinks the font only after that
  // (same fitTitle math there). Expanded blocks keep the class padding.
  const titlePadPx = expanded ? null : fitTitle(renderedHeight, textScale).padPx;

  // Mobile gets the new look (gradient text + custom-color body + priority
  // border/glow) instead of the desktop wash. Gate is cached/static per session.
  const mobile = isMobileUx();
  const mob = mobile
    ? mobileBlockStyle({
        priority: inheritedTask.priority,
        matrixColor: priorityColor,
        customColor: inheritedTask.custom_color,
        topicColors: index.topicColorsOf(task),
        calendarColor: index.calendarColorOf(task),
        isEvent: task.is_event,
      })
    : null;

  // Expanded: rise to the front, fill the column width, grow to fit content.
  // Always leave ~5% empty to the right (expanded or not). Pixels, not "%",
  // because framer-motion's `layout` needs concrete values.
  const left = expanded ? 2 : leftPx + 1;
  const rightPx = Math.max(2, Math.round(colWidthPx * 0.05));
  const zIndex = expanded ? 9999 : 10 + depth;
  // Inner content width: column minus the block's left inset, right gap, and the
  // horizontal padding (px-1.5 → 6px each side).
  const blockWidthPx = Math.max(0, colWidthPx - left - rightPx - 12);

  // Expand-phase transitions: springy on the expanded renders AND on the flip
  // render back (prevExpanded still holds the old value there); instant
  // everywhere else so zoom/nest-layout changes track the grid rigidly. An
  // in-flight spring keeps running across phase-off re-renders because framer
  // only re-reads the transition when the TARGET changes.
  //
  // Framer owns `height`/`left` ALWAYS, even though the transition is `instant`
  // outside the expand. Handing it those values only while the expand spring
  // needs them (plain inline styles otherwise) was measured and rejected: it is
  // worth ~230 ms of the phone pinch's main-thread time, but it also puts the
  // blocks' final geometry into the FIRST paint instead of one frame later, and
  // a dense-week cold start paid 700 → 825 ms to route content and 909 → 1089 ms
  // blocked for it (drift-controlled — the same before-build re-measured last
  // gave 709 ms). Cold start is the common path; the pinch is a gesture. See
  // `docs/calendar-view.md`. `tools/perf/check-expand.mjs` guards the animation
  // if anyone tries this again.
  const animPhase = (expanded || prevExpanded.current !== expanded) && !resizing;
  const instant = { duration: 0 };
  const expandSpring = reduce || !animPhase ? instant : springs.smooth;

  return (
      <motion.div
        data-task-block
        data-task-id={task.id}
        // No `layout`/`layoutId`: blocks do NOT fly between columns or spring to
        // layout changes. A day-shift slides whole columns (cheap), and a
        // drag-drop fades the old block out / the new one in (via DayColumn's
        // AnimatePresence). The hover-expand animates `height`/`left` as plain
        // values below.
        initial={reduce ? false : { opacity: 0 }}
        animate={{
          opacity: 1,
          height: expanded ? expandH ?? renderedHeight : renderedHeight,
          left,
        }}
        // Fade-out so a moved/removed block lingers (e.g. the old position after
        // a drag-drop) instead of vanishing instantly — but the duration is a
        // function of HOW MANY blocks are leaving, because this animation is
        // per block and holds every one of them in the DOM for its whole length.
        // A filter toggle that empties the visible week unmounts ~1,350 blocks
        // at once, and at the single-block duration their overlapping fades were
        // ~390 ms of that interaction's ~440 ms of blocked main thread.
        // `DayColumn` counts the removals for the commit and delivers the verdict
        // through `AnimatePresence`'s `custom`, which is the only channel an
        // already-exiting child has left (it no longer receives props).
        variants={EXIT_VARIANTS}
        exit={reduce ? "none" : "fade"}
        transition={reduce || resizing ? instant : { default: springs.smooth, height: expandSpring, left: expandSpring }}
        // `flex flex-col` so the inner colored block fills this container via
        // flex-stretch (the child uses `grow`, not `h-full`). `minHeight` is the
        // resting size in BOTH states — the animated height only ever grows past
        // it, never below it.
        className="absolute flex flex-col"
        style={{
          top,
          minHeight: renderedHeight,
          right: rightPx,
          zIndex,
        }}
        {...handlers}
      >
        <div
          ref={flashRef}
          // Native HTML5 DnD is desktop-only; on mobile the touch layer owns
          // moves. Leaving the node `draggable` lets the Android WebView start a
          // NATIVE drag on long-press, which Samsung turns into an "open in split
          // window" suggestion when dragged to the screen edge — so disable it.
          draggable={!mobile}
          onDragStart={(e) => {
            if (resizingRef.current) { e.preventDefault(); return; }
            setDragTaskId(e, task.id);
            setTaskDragging(true);
            // Record where within the block it was grabbed (minutes from its top)
            // so the drop keeps that point under the cursor, and hide the browser's
            // default ghost so only our live preview block shows.
            const rect = e.currentTarget.getBoundingClientRect();
            const offsetMin = ((e.clientY - rect.top) / hourHeight) * 60;
            beginBlockDrag(e, offsetMin, Math.max(renderedDur, 15), blockVisual);
            onExpandChange(false); // collapse while grabbed & moved
            // Deferred: mutating the dragged node synchronously inside
            // dragstart makes Chromium abort the drag.
            setTimeout(() => setDragSource(true), 0);
          }}
          onDragEnd={() => { setTaskDragging(false); endBlockDrag(); setDragSource(false); }}
          onDragOver={(e) => nestDrop.onDragOver(e, task)}
          onDragLeave={(e) => nestDrop.onDragLeave(e)}
          onDrop={(e) => nestDrop.onDrop(e, task)}
          onClick={(e) => {
            if (resizingRef.current) return;
            e.stopPropagation();
            onClick(task);
          }}
          onContextMenu={(e) => {
            if (suppressMobileContextMenu(e)) return;
            e.preventDefault();
            e.stopPropagation();
            // A right-click ends the casual-hover gesture: reset the dwell so an
            // action from the menu (e.g. Duplicate) that suddenly makes this
            // block narrower/`expandable` can't auto-expand it off the stale
            // hover. Collapse now, too, if it had already expanded.
            resetHover();
            onExpandChange(false);
            setCtxMenu({ x: e.clientX, y: e.clientY });
          }}
          title={task.virtual ? `${task.title} (recurring)` : task.title}
          style={{
            ...(mobile ? mob!.container : hasCustomStyle ? customStyle : undefined),
            ...(titlePadPx != null ? { paddingTop: titlePadPx, paddingBottom: titlePadPx } : null),
          }}
          className={cn(
            "group relative flex grow min-h-0 cursor-pointer flex-col overflow-hidden rounded border px-1.5 py-0.5 shadow-md transition-[transform,filter,box-shadow] duration-200 hover:ring-1 hover:ring-ring/40",
            expanded && "shadow-lg ring-1 ring-ring/40",
            mobile && "bg-card",
            !mobile && hasCustomStyle && "task-wash",
            !mobile && !hasCustomStyle && priorityToneClass(inheritedTask.priority, "block"),
            done && "opacity-50 line-through",
            snoozed && "opacity-60",
            (dragSource || touchMoving || resizingThis || sheetGhostSource) && "opacity-30",
            task.virtual && "border-dashed",
            armed && "z-50 scale-[1.05] shadow-lg brightness-110 ring-2 ring-primary",
            flash && "flash-highlight",
          )}
        >
          {!isContinuation && (
            <div
              onMouseDown={startResizeTop}
              onClick={(e) => e.stopPropagation()}
              draggable={false}
              className="absolute inset-x-0 top-0 z-10 h-1.5 cursor-ns-resize bg-current/20 opacity-0 hover:bg-current/40 group-hover:opacity-100"
              title={t("calendar.dragToMove")}
            />
          )}
          {inheritedTask.priority !== "none" && !isContinuation && !mobile && (
            <Flag
              aria-label={`${inheritedTask.priority} priority`}
              fill={PRIORITY_FLAG_HEX[inheritedTask.priority]}
              stroke="#ffffff"
              strokeWidth={2.5}
              // On a past event the flag drops UNDER the FaintOverlay (z-[1])
              // so it dims with the rest of the block instead of staying vivid.
              className={cn(
                "pointer-events-none absolute right-1 top-1 h-3.5 w-3.5 drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.45)]",
                !done && pastEvent ? "z-0" : "z-20",
              )}
            />
          )}
          {snoozed && !isContinuation && (
            <Moon
              aria-label={t("calendar.snoozedLabel")}
              className="pointer-events-none absolute left-1 top-1 z-20 h-3 w-3 drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.45)]"
              style={{ color: "var(--c-ink, currentColor)" }}
            />
          )}
          <TaskBlockContent
            task={task}
            blockHeightPx={renderedHeight}
            blockWidthPx={blockWidthPx}
            expanded={expanded}
            isContinuation={!!isContinuation}
            cover={expanded ? undefined : cover}
            onExpandableChange={setExpandable}
            autoExpandSubtasks
            mobileStyle={mob}
          />
          {task.recurrence_id && (expanded ? expandH ?? renderedHeight : renderedHeight) >= 30 && (
            // Series marker, bottom-right, inked in a near-shade of the block's
            // own fill (`--c-ink`, see block-wash) so it's visible on any color
            // without clashing. Below the FaintOverlay (z-[1]) → dims when past.
            // Hidden on very short blocks, where it would overlap the title.
            <Repeat
              aria-hidden
              className="pointer-events-none absolute bottom-2 right-1 z-0 h-2.5 w-2.5"
              style={{ color: "var(--c-ink, currentColor)" }}
            />
          )}
          <div
            onMouseDown={startResize}
            onClick={(e) => e.stopPropagation()}
            draggable={false}
            className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize bg-current/20 hover:bg-current/40"
            title={t("calendar.dragToResize")}
          />
          {!done && pastEvent && <FaintOverlay />}
        </div>
        <InsertButton taskId={task.id} />
        {ctxMenu && (
          <TaskContextMenu
            task={task}
            x={ctxMenu.x}
            y={ctxMenu.y}
            onClose={() => setCtxMenu(null)}
          />
        )}
      </motion.div>
  );
}
